/**
 * Shows the published release notes in the Produktnyheter view of the round "?" bubble
 * at the bottom left — Pendo's Resource Center, not Elevio.
 *
 * Pendo's own announcements are authored in their dashboard and cannot be repointed
 * from here, but the Resource Center renders into our DOM: their `<ol>` is the panel's
 * scroll container, so the releases go in as its first row and the two read as one
 * list. Everything here is keyed off structure rather than their hashed class names,
 * and if it stops matching nothing is inserted and their list shows as it does today.
 */
import T from 'Components/Helpers/translate';
import logError from 'Helpers/logError';
import openModal from 'App/services/Modal';
import {
	FRAME_SANDBOX,
	isProductNewsEnabled,
	productNewsFrameUrl,
	watchProductNewsFrame
} from 'App/helpers/productNewsPage';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';

/** The Resource Center's root, as Pendo ids it. */
const PENDO_CONTAINER_ID = 'pendo-resource-center-container';
const FRAME_ID = 'up-product-news-frame';

let observer: MutationObserver | null = null;
let frame: HTMLIFrameElement | null = null;
let row: HTMLLIElement | null = null;
let unwatchFrame: (() => void) | null = null;
let unavailable = false;
let driftLogged = false;
let markSeen: (() => void) | null = null;

/** Nothing to show: leave the panel as Pendo's own list and stop looking. */
const giveUp = (reason: string) => {
	unavailable = true;
	observer?.disconnect();
	observer = null;
	unwatchFrame?.();
	unwatchFrame = null;
	row?.remove();
	row = null;
	frame = null;
	logError(new Error(`Product news is unavailable: ${reason}`), 'Product news');
};

/**
 * Their markup moved: a sub-view listing rows, none of which is a guide. Logged once,
 * because otherwise this feature dies silently and nobody finds out.
 */
const reportDrift = (container: Element) => {
	if (driftLogged || !container.querySelector('ol li') || container.querySelector('ol li [id^="pendo-g-"]')) {
		return;
	}
	driftLogged = true;
	logError(new Error('Product news: the Pendo announcements list no longer matches'), 'Product news');
};

const insert = () => {
	const container = document.getElementById(PENDO_CONTAINER_ID);
	if (unavailable || !container || document.getElementById(FRAME_ID)) {
		return;
	}
	const list = container.querySelector('ol li [id^="pendo-g-"]')?.closest('ol');
	if (!list) {
		reportDrift(container);
		return;
	}
	const src = productNewsFrameUrl();
	if (!src) {
		return;
	}

	// Reaching here means the announcements view is open, so the reader is looking at the
	// news — whether or not it was announced to them. `markSeen` clears itself.
	markSeen?.();

	frame = document.createElement('iframe');
	frame.id = FRAME_ID;
	frame.title = T('productNews.title');
	frame.src = src;
	// Sized to its content, with the list doing the scrolling — so it must not scroll
	// itself, or the reader gets a scrollbar inside a scrollbar.
	frame.setAttribute('scrolling', 'no');
	frame.setAttribute('referrerpolicy', 'no-referrer');
	frame.setAttribute('sandbox', FRAME_SANDBOX);
	frame.style.cssText = 'width:100%;height:120px;border:0;display:block;background:#fff';
	unwatchFrame?.();
	unwatchFrame = watchProductNewsFrame(frame, {
		onHeight: height => {
			if (frame) {
				frame.style.height = `${height}px`;
			}
		},
		onUnavailable: giveUp
	});

	// Pendo's rows carry their own gutter, so copy it rather than restate it; the page
	// inside renders edge to edge and lines up with the rows below.
	row = document.createElement('li');
	const sibling = list.querySelector('li');
	if (sibling) {
		const { paddingLeft, paddingRight } = window.getComputedStyle(sibling);
		row.style.padding = `0 ${paddingRight} 0 ${paddingLeft}`;
	}
	row.style.listStyle = 'none';
	row.appendChild(frame);
	list.prepend(row);
};

/** Idempotent: safe to call more than once. No-op until a page is configured. */
export const installProductNews = () => {
	if (observer || unavailable || !isProductNewsEnabled()) {
		return;
	}
	// The panel is built and torn down on every open, so the whole body is watched.
	// Coalesced to a frame: Pendo's open animation produces mutations in bursts.
	let queued = false;
	observer = new MutationObserver(() => {
		if (queued) {
			return;
		}
		queued = true;
		window.requestAnimationFrame(() => {
			queued = false;
			insert();
		});
	});
	observer.observe(document.body, { childList: true, subtree: true });
	insert();
};

/**
 * The counterpart to install: drops the observer and the listener.
 *
 * The app never tears this down — it lives as long as the session does. Tests need it,
 * because an observer left watching `document.body` outlives the module instance that
 * created it and then races the next one.
 */
export const uninstallProductNews = () => {
	observer?.disconnect();
	observer = null;
	unwatchFrame?.();
	unwatchFrame = null;
	frame = null;
	row = null;
	unavailable = false;
	driftLogged = false;
	markSeen = null;
};

/**
 * Announce a release this user has not seen, which is only knowable once the app has its
 * data — so it arrives separately from the setup above. `onSeen` is called once, when the
 * reader has been shown the releases, here or in the panel.
 *
 * Comparing ids, not dates: a new release always takes a higher id, while re-publishing
 * an old one keeps its original date.
 */
export const announceProductNews = ({
	publishedIds,
	recentIds,
	lastSeenId,
	onSeen,
	readLastSeenId
}: {
	publishedIds: number[];
	recentIds: number[];
	lastSeenId: number;
	onSeen: (seenId: number) => void;
	/** The marker as the master DB holds it, rather than as the cached /alliwant body had it. */
	readLastSeenId: () => Promise<number>;
}) => {
	if (!isProductNewsEnabled() || !publishedIds?.length) {
		return;
	}

	// The marker is the highest published id, not the highest unread one: it means the
	// backlog behind an announcement counts as seen too.
	const highest = Math.max(...publishedIds, lastSeenId);
	markSeen = () => {
		markSeen = null;
		onSeen(highest);
	};

	if (!hasSoftDeployAccess('PRODUCT_NEWS_MODAL')) {
		return;
	}

	// Only an actual new release is announced unprompted. A reader who has never opened
	// the panel has the whole backlog unseen, and pushing that at them as news is wrong.
	if (!recentIds?.some(id => id > lastSeenId)) {
		return;
	}

	// `lastSeenId` arrives on the /alliwant body, which is cached in redis for 30 seconds
	// and is not invalidated when a user param is written — so a reader who dismisses this
	// and reloads inside that window would be shown the same release again. Confirmed
	// against the master DB before announcing, which costs a request only when there is
	// something to announce.
	// Opened through the registry rather than by importing the component: this file is on
	// the boot path, and a static import would pull the modal's chunk into it. It marks
	// them seen itself, once the digest is on screen — marking here would burn the
	// announcement on a chunk that fails to load.
	const announce = () => openModal('ProductNews', { onSeen: () => markSeen?.() });

	readLastSeenId()
		.then(confirmed => {
			if (recentIds.some(id => id > Math.max(lastSeenId, confirmed))) {
				announce();
			}
		})
		.catch(e => {
			// Announce anyway: showing a release twice beats never showing it.
			logError(e, 'Product news: could not confirm what this reader has seen');
			announce();
		});
};
